import React from 'react';
import { Grid, Typography, Menu, MenuItem, Button, Box } from '@mui/material';
import { InfoCard, SparkButton, SparkDataSearchBar } from 'sharedComponents/UI';
import EditableTitleSkeleton from 'sharedComponents/Skeleton/EditableTitleSkeleton';
import CreateMemoryModal from 'components/Memories/Modals/CreateMemoryModal';
import TypeDetailsContainer from './TypeDetailsContainer';
import { useMemoryPrivacyTypes } from 'hooks/EntityTypes/useMemoryPrivacyTypes';
import { useInfiniteMemoriesQuery, useMemorySearch } from 'hooks/memory';
import { useDebouncedState, useDialog, useResize } from 'hooks/common';
import type { DropdownOption } from 'types/common/DropdownOption';
import type { JoinedMemory } from 'types/Memory';
import { PlusIcon } from 'components/SVG';
import { getIsSearchableQuery } from 'utilities/index';
import StepSelectorItemSkeleton from 'sharedComponents/Skeleton/StepSelectorItemSkeleton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContextV2';
import MemorySearchFilters from './MemorySearchFilters';

const optionsOrder = ['All', 'Public', 'Private'];

type Props = {
  onClick: (selectedStepTypeID: string, resourceID?: string) => void;
  typeID: string;
};

const memoryTypeDropdownOptions = [
  { label: 'My Sources', value: 'mySources' },
  { label: 'Workspace Sources', value: 'workspaceSources' },
  { label: 'My Favourites', value: 'myFavouritesSources' },
];

function MemoryModal({ onClick, typeID }: Props) {
  const { isMobile } = useResize();
  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedState(query);
  const {
    joinedMemories,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteMemoriesQuery({}, !query);

  const { filter } = useSparkSearchContext();
  const [page, setPage] = React.useState(1);

  const { data: searchedMemories = [], isLoading: isSearchingForMemories } =
    useMemorySearch(
      { filters: filter, pagination: { pageNumber: page, pageSize: 10 } },
      { enabled: query.length >= 3 }
    );

  const [selectedPrivacyType, setSelectedPrivacyType] = React.useState('2');
  const { dropdownOptions, isLoading: isLoadingPrivacy } =
    useMemoryPrivacyTypes();
  const handleOptionSelect = (option: DropdownOption) => {
    setSelectedPrivacyType(`${option.value}`);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string | number) => {
    handleOptionSelect({ value: String(value), label: String(value) });
    handleClose();
  };

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isFetchingNextPage || !!query) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, query]);

  const memoriesToRender = React.useMemo(() => {
    const filterByPrivacyType = (memory: JoinedMemory) => {
      if (selectedPrivacyType === '2') {
        return true;
      }
      return memory.attributes.memoryPrivacyTypeID === +selectedPrivacyType;
    };

    if (searchedMemories.length) {
      return searchedMemories.filter(filterByPrivacyType);
    }

    return joinedMemories?.filter(filterByPrivacyType);
  }, [joinedMemories, searchedMemories, selectedPrivacyType]);

  const privacyTypes = dropdownOptions.map((option) => ({
    ...option,
    label: option.value === '2' ? 'All' : option.label,
  }));
  const reorderedPrivacyTypes = React.useMemo(
    () =>
      privacyTypes.sort((a, b) => {
        return optionsOrder.indexOf(a.label) - optionsOrder.indexOf(b.label);
      }),
    [privacyTypes]
  );

  const {
    closeDialog: closeCreateMemoryDialog,
    open: isCreateMemoryDialogOpen,
    openDialog: openCreateMemoryDialog,
  } = useDialog();

  const renderSearchResults = React.useMemo(() => {
    const isSearchableQuery = getIsSearchableQuery(query);

    if (!!query && !isSearchableQuery) {
      return (
        <Grid container mt={'16px'}>
          <InfoCard
            cardStyles={{ marginRight: '16px', width: '100%' }}
            info='Please enter at least 3 characters'
          />
        </Grid>
      );
    }

    if (isSearchableQuery && !searchedMemories.length) {
      if (isSearchingForMemories) {
        const NO_OF_ITEMS_ARRAY: number[] = [];
        for (let i = 1; i <= 6; i++) {
          NO_OF_ITEMS_ARRAY.push(i);
        }

        return (
          <Grid container>
            {NO_OF_ITEMS_ARRAY.map((value) => (
              <Grid item md={12} xl={6} padding={1}>
                <StepSelectorItemSkeleton />
              </Grid>
            ))}
          </Grid>
        );
      }
      return (
        <Grid container justifyContent='center' padding={2}>
          <InfoCard
            cardStyles={{ width: '100%' }}
            info={`0 results for "${query}"`}
            type='error'
          />
        </Grid>
      );
    }

    return (
      <TypeDetailsContainer
        onLastElementView={handleLatElementView}
        memories={memoriesToRender}
        isLoading={isLoading || isSearchingForMemories}
        onClick={onClick}
        typeID={typeID}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  }, [
    handleLatElementView,
    isFetchingNextPage,
    isLoading,
    isSearchingForMemories,
    memoriesToRender,
    onClick,
    query,
    searchedMemories.length,
    typeID,
  ]);

  return (
    <>
      <Grid container xs={12} justifyContent='space-between' minHeight={300}>
        <Grid item xs={12} gap={2.8}>
          <Grid item xs={12}>
            <Grid display={'flex'} gap={2} width={'100%'} marginY={3}>
              <MemorySearchFilters
                memoryTypeDropdownOptions={memoryTypeDropdownOptions}
              />
            </Grid>
            <Grid
              item
              container
              xs={12}
              display='flex'
              gap={isMobile ? 2 : 1}
              direction={isMobile ? 'column-reverse' : 'row'}
              justifyContent='space-between'
              flexWrap='nowrap'
            >
              {/* <Grid item xs={7}>
                {isLoadingPrivacy ? (
                  <EditableTitleSkeleton />
                ) : (
                  <>
                    <Button
                      id='filter-button'
                      aria-controls={open ? 'filter-menu' : undefined}
                      aria-haspopup='true'
                      aria-expanded={open ? 'true' : undefined}
                      onClick={handleClick}
                      endIcon={<KeyboardArrowDownIcon />}
                      sx={{
                        color: 'text.primary',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {
                        reorderedPrivacyTypes.find(
                          (option) => option.value === selectedPrivacyType
                        )?.label
                      }
                    </Button>
                    <Menu
                      id='filter-menu'
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      MenuListProps={{
                        'aria-labelledby': 'filter-button',
                      }}
                    >
                      {reorderedPrivacyTypes.map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => handleMenuItemClick(option.value)}
                          selected={option.value === selectedPrivacyType}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </Grid> */}
              <Grid
                item
                xs={isMobile ? 12 : 12}
                display='flex'
                justifyContent={isMobile ? 'center' : 'flex-end'}
              >
                <SparkButton
                  sx={{ padding: '10px 20px', marginRight: 2 }}
                  startIcon={<PlusIcon color='white' />}
                  variant='contained'
                  onClick={openCreateMemoryDialog}
                >
                  Add
                </SparkButton>
              </Grid>
            </Grid>
          </Grid>

          {renderSearchResults}
        </Grid>
      </Grid>
      {isCreateMemoryDialogOpen && (
        <CreateMemoryModal
          onClose={closeCreateMemoryDialog}
          open={isCreateMemoryDialogOpen}
          onMemoryAdd={(memoryID) => {
            onClick(typeID, memoryID);
          }}
        />
      )}
    </>
  );
}

export default MemoryModal;
